// Wire codes on the glass go through ONE door (design-system §3.9, D-441):
// `enumLabel(t, keyPrefix, code)` reads `<keyPrefix>.<code>` from whichever
// namespace already names that vocabulary (a floor conflict's reason is
// `production.conflicts.reason`). A code the catalogue has no key for yet
// reads as the translated word "Other" (`ref.unlisted`) and is logged once, so
// a reader never sees `unknown_reference` or `MACHINE_DRIFT` as typed on the
// wire, and a Russian or Chinese reader is not handed English words made from
// the code either (design-system §3.9; before it, the fallback was the code made readable,
// which is English in every language).
// There is no separate `enum.*` copy of a vocabulary an area already names:
// two copies cost every translator twice and drift apart.
//
// A code that is PACK DATA (a waste cause, a downtime reason) has a name in
// its own catalogue, and a screen that can read that catalogue shows the name
// first; this helper is the fallback for the reader who cannot.

import type { TFunction } from "i18next";

/** "MACHINE_DRIFT" → "Machine drift"; "wcause.strand_break" → "Strand break". */
export function readableCode(code: string): string {
  const last = code.split(".").pop() ?? code;
  const words = last.replace(/[_-]+/g, " ").trim().toLowerCase();
  return words.length === 0 ? code : words.charAt(0).toUpperCase() + words.slice(1);
}

/** Keys already reported as missing, so a long list logs each one once. */
const reported = new Set<string>();

/** The catalogue name of `code` under `keyPrefix`, else the reader's "Other".
 * A missing code is no code at all, so it reads as the absent mark. */
export function enumLabel(
  t: TFunction,
  keyPrefix: string,
  code: string | null | undefined,
): string {
  if (code === null || code === undefined || code === "") return "—";
  const key = `${keyPrefix}.${code}`;
  const missing = "\u0000";
  const label = t(key, { defaultValue: missing });
  if (label !== missing && label.length > 0) return label;
  if (!reported.has(key)) {
    reported.add(key);
    console.warn(`enumLabel: no name for "${key}" (${readableCode(code)}); showing "Other"`);
  }
  return t("ref.unlisted", { defaultValue: "Other" });
}
